import {
  Card,
  P,
  PrimaryCTAButton,
  Radio,
  RadioField,
  TextLink,
} from '@ovotech/nebula';
import { useAnalytics } from '@ovotech/ui-tools';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom-v5';
import styled from 'styled-components';
import { SectionHeader } from '@/src/pages/AnytimeEligibilityV2/components/SectionHeader';
import { ROUTE_ANYTIME_ELIGIBILITY } from '@/src/constants/routes';
import { hasSMETS2Meters } from '@/src/pages/Renewal/selectors';
import { ReactComponent as SmartChargerIcon } from '@/src/resources/smart-charger.svg';
import { useFeature } from '@/src/utils/optimizely';
import {
  ALLOW_ANYTIME_SIGNUP,
  CHARGER_MODEL_SELECTION,
} from '@/src/constants/feature-flags';
import {
  EV,
  EVEC_COMPATIBILITY_URL,
  SmartChargers,
  useChargeAnytimeContext,
  useChargerOptions,
  useChargeAnytimeDevices,
} from '@monovo/ev-anytime';
import { PageHeader } from '@/src/pages/AnytimeEligibilityV2/components/PageHeader';
import { PageWithNavigation } from '@/src/components';
import { JourneyUnavailable } from '@/src/pages/AnytimeEligibilityV2/components/JourneyUnavailable';
import { Loading } from '@/src/pages/AnytimeEligibilityV2/components/Loading';
import { TechnicalGlitchPage } from '@/src/pages/AnytimeEligibilityV2/pages/TechnicalGlitchPage/TechnicalGlitchPage';
import { Margin, InfoNotification } from '@ovotech/element-v4';
import { SmartChargerWithModelSelection } from '@/src/pages/AnytimeEligibilityV2/pages/SmartChargerQuestions/SmartChargerWithModelSelection';

const StyledPrimaryCTAButton = styled(PrimaryCTAButton)`
  background-color: #00261c;
`;

const CTAButtonContainer = styled.div`
  margin-top: var(--space-600);
  display: flex;
  justify-content: flex-end;
`;

const SectionContainer = styled.div`
  margin-bottom: 25px;
`;

const StyledP = styled(P)`
  color: #2f3749;
`;

const StyledPWithSpacing = styled(StyledP)`
  margin-bottom: var(--space-300);
`;

export const SmartChargerQuestions = () => {
  const history = useHistory();
  const { dispatch: analyticsDispatch } = useAnalytics();
  const [allowAnytimeSignUp] = useFeature(ALLOW_ANYTIME_SIGNUP);
  const [isChargerModelSelectionEnabled] = useFeature(CHARGER_MODEL_SELECTION);
  const { chargers, isLoading, isError } = useChargerOptions();
  const firstCharger = chargers[0];
  const { models } = useChargerOptions();

  const {
    smartCharger,
    setSmartCharger,
    hasSmartMeter,
    smartChargerModel,
    chargerDeviceId,
  } = useChargeAnytimeContext();

  const [formErrors, setFormErrors] = useState<{
    smartCharger: boolean;
    smartChargerModel: boolean;
  }>({ smartCharger: false, smartChargerModel: false });

  const hasSMETS2SmartMeter = useSelector(hasSMETS2Meters);
  const { data: chargersData } = useChargeAnytimeDevices('chargers');
  const chargerDevicesMap = new Map(
    chargersData?.devices.map(d => [d.manufacturer, d]) ?? [],
  );

  useEffect(() => {
    if (
      !smartCharger &&
      firstCharger?.value &&
      !isLoading &&
      !isChargerModelSelectionEnabled
    ) {
      setSmartCharger(firstCharger.value);
    }
  }, [
    setSmartCharger,
    smartCharger,
    firstCharger,
    isLoading,
    isChargerModelSelectionEnabled,
  ]);

  useEffect(() => {
    setFormErrors({ smartCharger: false, smartChargerModel: false });
  }, [smartCharger, smartChargerModel]);

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: EV.ELIGIBILITY
        .EVS_OVO_CHARGE_ANYTIME_ELIGIBILITY_SMART_CHARGER_VIEWED,
      properties: {
        hasNoSMETS2SmartMeter: !hasSMETS2SmartMeter,
        hasNoSmartMeter: !hasSmartMeter,
      },
    });
    // `analyticsDispatch` method is not memoized and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSmartMeter, hasSMETS2SmartMeter]);

  useEffect(() => {
    analyticsDispatch({
      type: 'view',
      name: 'EVS - EVS OVO Drive Anytime Smartcharger Eligibility Viewed',
      properties: {
        hasNoSMETS2SmartMeter: !hasSMETS2SmartMeter,
        hasNoSmartMeter: !hasSmartMeter,
      },
    });
    // `analyticsDispatch` method is not memoized and it causes useless calls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSmartMeter, hasSMETS2SmartMeter]);

  const goToNextPage = () => {
    if (!smartCharger) {
      setFormErrors({ smartCharger: true, smartChargerModel: false });
      return;
    }

    if (
      isChargerModelSelectionEnabled &&
      models(smartCharger).length > 0 &&
      !smartChargerModel
    ) {
      setFormErrors({ smartCharger: false, smartChargerModel: true });
      return;
    }

    analyticsDispatch({
      type: 'click',
      name: EV.ELIGIBILITY
        .EVS_OVO_CHARGE_ANYTIME_ELIGIBILITY_SMART_CHARGER_SUBMIT_CLICKED,
      properties: {
        chargerName: smartCharger,
        chargerModel: smartChargerModel,
        chargerDeviceId,
      },
    });

    analyticsDispatch({
      type: 'click',
      name: 'EVS - EVS OVO Charge Anytime Charger Select submit clicked',
      properties: {
        chargerName: smartCharger,
        chargerModel: smartChargerModel,
        chargerDeviceId,
      },
    });

    history.push(ROUTE_ANYTIME_ELIGIBILITY.VEHICLE_AND_HOME_SETUP);
  };

  if (isError) {
    return <TechnicalGlitchPage header={'Check your set-up'} />;
  }

  if (isLoading) {
    return <Loading />;
  }

  return (
    <PageWithNavigation title="EV Charge Anytime - Eligibility">
      {allowAnytimeSignUp ? (
        <>
          <PageHeader
            title="Check your set-up"
            showDescription={false}
            testId="check-eligibility-header"
            titleLevel="h4"
          />
          <Card data-testid="eligibility-questions-body">
            <SectionContainer>
              <SectionHeader Icon={SmartChargerIcon}>
                What EV charger do you have at home?
              </SectionHeader>
              <StyledPWithSpacing>
                Don’t worry if your charger isn’t listed below, or if you don’t
                have one. You can still connect to Charge Anytime, as long as
                the rest of your set-up is compatible.
              </StyledPWithSpacing>
              <StyledP>
                You’ll find the model name on a sticker on the side of your
                charger.
              </StyledP>

              {!isChargerModelSelectionEnabled && (
                <RadioField label="" name="selected">
                  {chargers.map(charger => {
                    const device = chargerDevicesMap.get(charger.value);

                    const isBeta = device?.status === 'BETA_COMPATIBLE';
                    return (
                      <div
                        key={charger.value}
                        data-testid={`radio-button-${charger.value}`}
                      >
                        <Radio
                          id={charger.value}
                          value={charger.value}
                          label={`${charger.text}${isBeta ? ' (Beta)' : ''}`}
                          checked={smartCharger === charger.value}
                          onChange={() => setSmartCharger(charger.value)}
                        />
                      </div>
                    );
                  })}
                </RadioField>
              )}
              {isChargerModelSelectionEnabled && (
                <SmartChargerWithModelSelection
                  manufacturerError={
                    formErrors.smartCharger ? 'Make is required' : ''
                  }
                  modelError={
                    formErrors.smartChargerModel ? 'Model is required' : ''
                  }
                />
              )}

              {smartCharger === SmartChargers.EVEC && (
                <Margin top={4}>
                  <InfoNotification title={'Please note'} id={'evec-info'}>
                    <P>
                      Some evec chargers are not compatible, and your firmware
                      must be up to date -{' '}
                      <TextLink opensInNewWindow href={EVEC_COMPATIBILITY_URL}>
                        find out more
                      </TextLink>
                      .
                      <br />
                      If your charger isn't compatible, select 'My charger model
                      isn`t listed' and go to the next step to check if your
                      vehicle is compatible.
                    </P>
                  </InfoNotification>
                </Margin>
              )}
            </SectionContainer>
            <CTAButtonContainer>
              <StyledPrimaryCTAButton
                data-testid="next-button"
                onClick={goToNextPage}
              >
                Check set-up
              </StyledPrimaryCTAButton>
            </CTAButtonContainer>
          </Card>
        </>
      ) : (
        <JourneyUnavailable />
      )}
    </PageWithNavigation>
  );
};
